import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";
import Notification from "../../components/Notification/Notification";
import "./BudgetsEdit.css";

const BudgetsEdit = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const budgetId = location?.state?.budgetId;
    const scope = location?.state?.scope;

    const [locationName, setLocationName] = useState("");
    const [formData, setFormData] = useState({ year: "", type: "Original", revisionNumber: "", remark: "" });
    const [hasBudgetLines, setHasBudgetLines] = useState(false);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [yearOptions, setYearOptions] = useState([]);
    const [notification, setNotification] = useState({ show: false, message: "", type: "info" });

    useEffect(() => {
        const cur = new Date().getFullYear();
        setYearOptions(Array.from({ length: 5 }, (_, i) => cur - i));
    }, []);

    useEffect(() => {
        if (!budgetId) return;
        (async () => {
            try {
                setLoading(true);
                const { data } = await privateApi.get(`/api/${scope}/budgets/${budgetId}`);
                const b = data.data;
                setLocationName(b.stateId?.name || b.lgaId?.name || "");
                setHasBudgetLines(b.budgetLinesCount > 0);
                setFormData({ year: b.year ?? "", type: b.type ?? "Original", revisionNumber: b.revisionNumber ?? "", remark: b.remark ?? "" });
            } catch (err) {
                setNotification({ show: true, message: err.response?.data?.message || "Failed to load budget.", type: "error" });
            } finally { setLoading(false); }
        })();
    }, [budgetId, scope]);

    const handleSubmit = async e => {
        e.preventDefault();
        setActionLoading(true);

        try {
            const payload = {
                year: Number(formData.year),
                remark: formData.remark
            };

            const { data } = await privateApi.patch(
                `/api/${scope}/budgets/${budgetId}`,
                payload
            );

            setNotification({
                show: true,
                message: `Budget ${data.data.type} ${data.data.year} rev ${data.data.revisionNumber} updated successfully!`,
                type: "success"
            });

            setTimeout(() => {
                navigate(-1);
            }, 2000);

        } catch (err) {
            setNotification({
                show: true,
                message: err.response?.data?.message || "Failed to update budget.",
                type: "error"
            });
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <Spinner loadingInfo="Loading Budget Details…" />;

    return (
        <div className="bgt-page-centered">
            <div className="be-inner">
                <Notification show={notification.show} message={notification.message}
                    type={notification.type} onClose={() => setNotification(p => ({ ...p, show: false }))} />

                <div className="bgt-card" style={{ animation: "bgt-fadeUp .45s ease both" }}>
                    <div className="bgt-card-header">
                        <h2>Edit Budget</h2>
                        <p>Update remark for this fiscal budget</p>
                    </div>

                    <form className="bgt-form-body" onSubmit={handleSubmit}>
                        {locationName && (
                            <div className="be-location-chip">📍 {locationName}</div>
                        )}

                        <div className="bgt-section-title">Budget Information</div>

                        <div className="bgt-form-group">
                            <label className="bgt-label required">Fiscal Year</label>
                            <select className="bgt-select" name="year" value={formData.year}
                                onChange={e => setFormData(p => ({ ...p, year: e.target.value }))}
                                disabled={hasBudgetLines} required>
                                <option value="">Select Year…</option>
                                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            {hasBudgetLines && <p className="be-disabled-note">Year is locked — budget lines exist.</p>}
                        </div>

                        <div className="bgt-form-group">
                            <label className="bgt-label">Budget Type</label>
                            <select className="bgt-select" name="type" value={formData.type}
                                onChange={e => setFormData(p => ({ ...p, type: e.target.value }))}
                                disabled={hasBudgetLines}>
                                <option value="Original">Original</option>
                                <option value="Supplementary">Supplementary</option>
                            </select>
                            {hasBudgetLines && <p className="be-disabled-note">Type is locked — budget lines exist.</p>}
                        </div>

                        <div className="bgt-form-group">
                            <label className="bgt-label">Revision Number</label>
                            <div className="be-readonly-field">{formData.revisionNumber || "—"}</div>
                        </div>

                        <div className="bgt-form-group">
                            <label className="bgt-label">Remark</label>
                            <textarea className="bgt-textarea" value={formData.remark}
                                onChange={e => setFormData(p => ({ ...p, remark: e.target.value }))}
                                placeholder="Optional remark…" />
                        </div>

                        <div className="bgt-form-actions">
                            <button type="button" className="bgt-btn bgt-btn-secondary" onClick={() => navigate(-1)}>← Back</button>
                            <button type="submit" className="bgt-btn bgt-btn-primary" disabled={actionLoading}>
                                {actionLoading ? "Saving…" : "✓ Save Changes"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BudgetsEdit;